import React from 'react';
import { AlertTriangle, Info, AlertCircle, Clock } from 'lucide-react';
import type { DeadlineFinding } from '../../services/api';

interface DeadlineClausesFlagCardProps {
  finding: DeadlineFinding;
}

const urgencyConfig: Record<string, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  high: {
    label: 'High',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    icon: <AlertCircle className="h-4 w-4" />,
  },
  medium: {
    label: 'Medium',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    icon: <AlertTriangle className="h-4 w-4" />,
  },
  low: {
    label: 'Low',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    icon: <Info className="h-4 w-4" />,
  },
};

const categoryLabels: Record<string, string> = {
  immediate: 'Immediate',
  hours: 'Hours',
  days_numeric: 'Days (Numeric)',
  days_written: 'Days (Written)',
  weeks: 'Weeks',
  months: 'Months',
  years: 'Years',
  calendar_date: 'Calendar Date',
  no_later_than: 'No Later Than',
  recurring: 'Recurring',
};

function highlightMatch(text: string, start: number, end: number): React.ReactNode {
  if (start < 0 || end > text.length || start >= end) {
    return text;
  }
  return (
    <>
      {text.slice(0, start)}
      <mark className="bg-yellow-200 px-0.5 rounded" data-testid="highlight-match">
        {text.slice(start, end)}
      </mark>
      {text.slice(end)}
    </>
  );
}

function formatDays(days: number | null): string | null {
  if (days === null) return null;
  if (days === 0) return 'Immediate';
  if (days < 1) return `${Math.round(days * 24)} hours`;
  if (days < 7) return `${Math.round(days)} day${Math.round(days) !== 1 ? 's' : ''}`;
  if (days < 30) return `${Math.round(days / 7)} week${Math.round(days / 7) !== 1 ? 's' : ''}`;
  if (days < 365) return `${Math.round(days / 30)} month${Math.round(days / 30) !== 1 ? 's' : ''}`;
  return `${Math.round(days / 365)} year${Math.round(days / 365) !== 1 ? 's' : ''}`;
}

export const DeadlineClausesFlagCard: React.FC<DeadlineClausesFlagCardProps> = ({
  finding,
}) => {
  const urgency = urgencyConfig[finding.urgency] || urgencyConfig.low;
  const categoryLabel = categoryLabels[finding.category] || finding.category;
  const formattedDays = formatDays(finding.normalizedDays);

  return (
    <div
      className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
      data-testid={`deadline-flag-card-${finding.id}`}
    >
      {/* Header: urgency badge + category chip + normalized time */}
      <div className="flex items-center gap-2 mb-3">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${urgency.bgColor} ${urgency.color}`}
          data-testid="urgency-badge"
        >
          {urgency.icon}
          {urgency.label}
        </span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
          {categoryLabel}
        </span>
        {formattedDays && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
            <Clock className="h-3 w-3" />
            {formattedDays}
          </span>
        )}
        <span className="text-xs text-gray-400 ml-auto">
          {finding.ruleName}
        </span>
      </div>

      {/* Clause text with highlighted match */}
      <div className="text-sm text-gray-700 mb-3 leading-relaxed bg-gray-50 p-3 rounded" data-testid="clause-text">
        {highlightMatch(finding.clauseText, finding.charOffsetStart, finding.charOffsetEnd)}
      </div>

      {/* Rationale */}
      <p className="text-sm text-gray-600 italic" data-testid="rationale">
        {finding.rationale}
      </p>
    </div>
  );
};
